import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Product, Brand, ProductVariation } from '@/types/database';

interface ProductRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  short_description: string | null;
  price: number;
  sale_price: number | null;
  sku: string | null;
  stock_status: string;
  stock_quantity: number | null;
  brand_id: string | null;
  featured: boolean;
  images: unknown;
  specifications: unknown;
  meta_title: string | null;
  meta_description: string | null;
  created_at: string;
  updated_at: string;
  brand: Brand | null;
  variations: ProductVariation[];
}

function mapProductRow(row: ProductRow): Product {
  return {
    ...row,
    stock_status: row.stock_status as Product['stock_status'],
    images: Array.isArray(row.images) ? row.images : [],
    specifications: typeof row.specifications === 'object' && row.specifications !== null 
      ? row.specifications as Record<string, string> 
      : {},
    brand: row.brand || undefined,
    variations: row.variations || [],
  };
}

export const useFeaturedProducts = (limit: number = 4) => {
  return useQuery({
    queryKey: ['featured-products', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          brand:brands(*),
          variations:product_variations(*)
        `)
        .eq('featured', true);

      if (error) throw error;
      
      if (!data || data.length === 0) {
        return [];
      }
      
      // Si hay más productos que el límite, seleccionar aleatoriamente
      const products = (data as ProductRow[]).map(mapProductRow);
      
      if (products.length > limit) {
        const shuffled = [...products].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, limit);
      }
      
      return products;
    },
  });
};
